import classNames from "classnames";
import dayjs from "dayjs";
import { SetStateAction, useEffect, useState } from "react";
import { useScrollIntoView } from "@mantine/hooks";
import { ChevronRight } from "@/public/icons/ChevronRight";
import styles from "./CalendarNav.module.scss";
import { UnpaidInvoice } from "../../AccountB2B";

type UnpaidTotal = {
  month: number;
  total: number;
};

export type CalendarNavProps = {
  unpaidData: UnpaidInvoice[] | undefined;
  month: number;
  setMonth: React.Dispatch<SetStateAction<number>>;
  year: number;
  setYear: React.Dispatch<SetStateAction<number>>;
};

export const CalendarNav = ({
  unpaidData,
  month,
  setMonth,
  year,
  setYear,
}: CalendarNavProps) => {
  const months: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [unpaidTotals, setUnpaidTotals] = useState<UnpaidTotal[]>([]);

  const { scrollIntoView, targetRef, scrollableRef } =
    useScrollIntoView<HTMLDivElement>({
      axis: "x",
    });

  useEffect(() => {
    scrollIntoView({ alignment: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    const tmp: UnpaidTotal[] = [];
    setIsLoading(true);

    unpaidData
      ?.filter((data) => data.balance > 0)
      .map((ud) => {
        const index = tmp.findIndex(
          (ut) => ut.month === dayjs(ud.invoiceDate).get("month")
        );
        if (index === -1) {
          tmp.push({
            month: dayjs(ud.invoiceDate).get("month"),
            total: ud.balance,
          });
        } else {
          tmp[index].total += ud.balance;
        }

        setUnpaidTotals(tmp);
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unpaidData]);

  return (
    <div className={styles.calendarNav}>
      <div className={styles.year}>
        <div className={styles.chevronLeft} onClick={() => setYear(year - 1)}>
          <ChevronRight size="24" color={"#062e52"} />
        </div>
        <div className={styles.yearLabel}>{year}</div>
        {year + 1 <= dayjs().get("year") ? (
          <div
            className={styles.chevronRight}
            onClick={() => setYear(year + 1)}
          >
            <ChevronRight size="24" color={"#062e52"} />
          </div>
        ) : (
          <div className={styles.chevronRight}></div>
        )}
      </div>
      <div className={styles.months} ref={scrollableRef}>
        <div className={styles.scrollableTabs}>
          {months.map((m: number) => (
            <div
              className={classNames(styles.month, {
                [styles.active]: m === month,
              })}
              onClick={() => setMonth(m)}
              ref={m === month ? targetRef : undefined}
              key={`month-${m}`}
            >
              <div className={styles.label}>
                {dayjs().month(m).format("MMM")}
              </div>
              {!isLoading && unpaidTotals.find((ut) => ut.month === m) && (
                <div className={styles.unpaid}>
                  {unpaidTotals
                    .find((ut) => ut.month === m)
                    ?.total.toFixed(2)
                    .replace(/[.,]00$/, "")}{" "}
                  €
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
